import { FILL_VALUES } from '../shared/fill-values'
import callNotesSchema from './call-notes.schema.json'
import invoiceData from './invoice-data.json'
import invoiceSchema from './invoice.schema.json'

// Tour registry for the SPA (spec §2). The proxy owns the /v1 bodies; this only
// knows what the visitor sees: titles, samples, editable params, snippets.
import type { DemoId, DemoKind, DemoParams } from '@/shared/contract'

// Mirrors src/modules/proxy/demos.ts REDACT_CATEGORIES (the proxy caps at 10).
export const REDACT_CATEGORIES = [
  'Person names',
  'Address',
  'Phone number',
  'Email address',
  'Date of birth',
  'Health insurance number',
]

// A stop is one tour card. Stops 1 and 2 share the parse-md run (view differs).
export type StopId = DemoId | 'parse-blocks'

export interface Stop {
  id: StopId
  demo: DemoId
  n: number
  shortTitle: string
  title: string
  blurb: string
  verb: 'parse' | 'extract' | 'redact' | 'compose' | 'fill'
  kind: DemoKind
  accept: string
  /** Sample input file under /samples (undefined for compose). */
  sample?: { file: string; label: string }
  /** Extra sample assets the snippet references (fill template). */
  template?: string
  /** Names of the step ticker while the run is live. */
  steps: string[]
  defaultParams: DemoParams[DemoId]
}

const PDF =
  'application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.odt,.rtf,.txt,.md,.html'
const AUDIO = 'audio/mpeg,audio/wav,audio/mp4,audio/webm,.mp3,.wav,.m4a,.webm'

export const STOPS: Stop[] = [
  {
    id: 'parse-md',
    demo: 'parse-md',
    n: 1,
    shortTitle: 'Markdown',
    title: 'Turn a PDF into Markdown',
    blurb: 'One call. Clean Markdown with tables, ready for an LLM.',
    verb: 'parse',
    kind: 'pdf',
    accept: PDF,
    sample: { file: 'sample-manufacturing-invoice.pdf', label: 'Manufacturing invoice' },
    steps: ['Upload', 'Parse'],
    defaultParams: {},
  },
  {
    id: 'parse-blocks',
    demo: 'parse-md',
    n: 2,
    shortTitle: 'Blocks',
    title: 'See every block with coordinates',
    blurb: 'The same run also returns JSON: every text block with its box on the page.',
    verb: 'parse',
    kind: 'pdf',
    accept: PDF,
    sample: { file: 'sample-manufacturing-invoice.pdf', label: 'Manufacturing invoice' },
    steps: ['Upload', 'Parse'],
    defaultParams: {},
  },
  {
    id: 'parse-ocr',
    demo: 'parse-ocr',
    n: 3,
    shortTitle: 'OCR',
    title: 'Scanned page? Toggle OCR',
    blurb: 'A scan has no text layer. processing: "ocr" reads the pixels instead.',
    verb: 'parse',
    kind: 'pdf',
    accept: PDF,
    sample: { file: 'sample-invoice-scanned.pdf', label: 'Scanned invoice' },
    steps: ['Upload', 'Parse (simple)', 'Parse (OCR)'],
    defaultParams: {},
  },
  {
    id: 'extract-invoice',
    demo: 'extract-invoice',
    n: 4,
    shortTitle: 'Extract',
    title: 'Extract an invoice as JSON you define',
    blurb: 'Send a JSON Schema. Get values back, each one cited to its spot on the page.',
    verb: 'extract',
    kind: 'pdf',
    accept: PDF,
    sample: { file: 'sample-manufacturing-invoice.pdf', label: 'Manufacturing invoice' },
    steps: ['Upload', 'Parse', 'Extract', 'Cite'],
    defaultParams: { schema: invoiceSchema as Record<string, unknown> },
  },
  {
    id: 'transcribe',
    demo: 'transcribe',
    n: 5,
    shortTitle: 'Transcript',
    title: 'Transcribe a call, with speakers',
    blurb: 'Audio in, diarized transcript out. Same parse verb.',
    verb: 'parse',
    kind: 'audio',
    accept: AUDIO,
    sample: { file: 'sample-followup-call-labresults.mp3', label: 'Follow-up call (57 s)' },
    steps: ['Upload', 'Transcribe', 'Diarize'],
    defaultParams: {},
  },
  {
    id: 'call-notes',
    demo: 'call-notes',
    n: 6,
    shortTitle: 'Notes',
    title: 'Turn the call into clinical notes',
    blurb: 'Extract from audio. Every note field points at the second it was said.',
    verb: 'extract',
    kind: 'audio',
    accept: AUDIO,
    sample: { file: 'sample-followup-call-labresults.mp3', label: 'Follow-up call (57 s)' },
    steps: ['Upload', 'Transcribe', 'Extract', 'Cite'],
    defaultParams: { schema: callNotesSchema as Record<string, unknown> },
  },
  {
    id: 'redact',
    demo: 'redact',
    n: 7,
    shortTitle: 'Redact',
    title: 'Redact a document',
    blurb: 'Pick the categories. Get a new PDF with the PII burned out.',
    verb: 'redact',
    kind: 'pdf',
    accept: PDF,
    sample: { file: 'sample-patient-intake.pdf', label: 'Patient intake form' },
    steps: ['Upload', 'Parse', 'Detect', 'Redact'],
    defaultParams: { categories: REDACT_CATEGORIES },
  },
  {
    id: 'compose-invoice',
    demo: 'compose-invoice',
    n: 8,
    shortTitle: 'Compose',
    title: 'Generate a PDF from data',
    blurb: 'JSON in, PDF out, synchronously. Edit the total and re-run.',
    verb: 'compose',
    kind: 'none',
    accept: '',
    steps: ['Compose'],
    defaultParams: { data: invoiceData as Record<string, unknown> },
  },
  {
    id: 'fill-form',
    demo: 'fill-form',
    n: 9,
    shortTitle: 'Fill',
    title: 'Fill a form with values',
    blurb:
      'Edit the field values, then fill the sample certificate. No source document or model call.',
    verb: 'fill',
    kind: 'none',
    accept: '',
    template: 'sample-certificate-blank.pdf',
    steps: ['Prepare template', 'Fill'],
    defaultParams: { values: FILL_VALUES },
  },
]

export const stopById = (id: string | undefined): Stop =>
  STOPS.find((s) => s.id === id) ?? STOPS[0]!

export const sampleUrl = (origin: string, file: string) => `${origin}/samples/${file}`
